import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import api, { formatApiErrorDetail } from '../../lib/api';
import { toast } from 'sonner';

// Generic CRUD manager for events / programs / board members
const saveButtonLabel = (saving, editing, itemLabel) => {
  if (saving) return 'Saving...';
  if (editing) return 'Save Changes';
  return `Create ${itemLabel}`;
};

export const ResourceAdmin = ({ resource, listEndpoint, title, itemLabel, fields, renderSummary }) => {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(listEndpoint).then((res) => setItems(res.data || [])).catch(() => {});
  }, [listEndpoint]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({});
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    const initial = {};
    fields.forEach((f) => {
      const v = item[f.name];
      initial[f.name] = f.type === 'list' ? (v || []).join('\n') : v ?? '';
    });
    setForm(initial);
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {};
    fields.forEach((f) => {
      let v = form[f.name];
      if (v === '' || v === undefined) return;
      if (f.type === 'list') v = String(v).split('\n').map((s) => s.trim()).filter(Boolean);
      if (f.type === 'number') v = Number(v);
      payload[f.name] = v;
    });
    try {
      if (editing) {
        await api.put(`/admin/${resource}/${editing.id}`, payload);
        toast.success(`${itemLabel} updated`);
      } else {
        await api.post(`/admin/${resource}`, payload);
        toast.success(`${itemLabel} created`);
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete this ${itemLabel.toLowerCase()}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/${resource}/${item.id}`);
      toast.success(`${itemLabel} deleted`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid={`admin-${resource}-panel`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-xl text-[#3c4a2a]">{title}</h2>
        <Button
          onClick={openCreate}
          data-testid={`admin-${resource}-add-btn`}
          className="bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add {itemLabel}
        </Button>
      </div>
      <div className="space-y-3">
        {items.map((item) => (
          <Card key={item.id} className="border border-[#5f6d51]/10" data-testid={`admin-${resource}-row-${item.id}`}>
            <CardContent className="p-4 flex items-center justify-between gap-4">
              <div className="min-w-0">{renderSummary(item)}</div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => openEdit(item)}
                  data-testid={`admin-${resource}-edit-${item.id}`}
                  className="border-[#5d6b3c]/40 text-[#5d6b3c]"
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleDelete(item)}
                  data-testid={`admin-${resource}-delete-${item.id}`}
                  className="border-[#d84f5b]/40 text-[#d84f5b] hover:bg-[#d84f5b]/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && (
          <p className="text-[#5f6d51]/60 text-sm py-8 text-center">No items yet.</p>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`admin-${resource}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#3c4a2a]">
              {editing ? `Edit ${itemLabel}` : `New ${itemLabel}`}
            </DialogTitle>
            <DialogDescription>
              {editing ? 'Update the fields below and save your changes.' : 'Fill in the details below.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            {fields.map((f) => (
              <div key={f.name}>
                <Label htmlFor={`f-${f.name}`} className="text-[#3c4a2a]">
                  {f.label}{f.required ? ' *' : ''}
                </Label>
                {f.type === 'textarea' || f.type === 'list' ? (
                  <Textarea
                    id={`f-${f.name}`}
                    value={form[f.name] ?? ''}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    required={f.required && !editing}
                    rows={f.type === 'list' ? 4 : 3}
                    data-testid={`admin-field-${f.name}`}
                    className="mt-2 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
                    placeholder={f.placeholder || ''}
                  />
                ) : (
                  <Input
                    id={`f-${f.name}`}
                    type={f.type === 'number' ? 'number' : 'text'}
                    value={form[f.name] ?? ''}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    required={f.required && !editing}
                    data-testid={`admin-field-${f.name}`}
                    className="mt-2 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
                    placeholder={f.placeholder || ''}
                  />
                )}
                {f.hint && <p className="text-xs text-[#5f6d51]/60 mt-1">{f.hint}</p>}
              </div>
            ))}
            <Button
              type="submit"
              disabled={saving}
              data-testid={`admin-${resource}-save-btn`}
              className="w-full bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white"
            >
              {saveButtonLabel(saving, editing, itemLabel)}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
